// Owner: views-grid. Keys of the spreadsheet grid (plan §4.2, grid scope): arrows, ⌘-arrows, Home/End,
// PgUp/PgDn, ⇧ extends; Enter/F2 edit; Tab/⇧Tab; Space peeks; ⇧Space selects the row; ⌘D fills down;
// Delete clears; ⌘↵ opens the record; Esc clears the range; ⌘A selects all; ⌘Z / ⌘⇧Z revert record
// changes (the list scope's server undo). Typing a character starts editing (the grid handles printable keys itself). ⌘F focuses the
// list's search (route scope of the object list). Discovered by glob; keep it tiny.
import { defineCommand, defineScope, serverUndoCommands } from '@dataroom/interaction';
import { defineAppCommands } from '../../../extensions/commands';
import { t } from '../../../lib/i18n';
import type { Move } from './selection';

export type GridCommandContext = {
  move: (move: Move, extend: boolean) => void;
  /** Tab / ⇧Tab; false at the grid's ends (focus leaves the grid). */
  tab: (dir: 1 | -1) => boolean;
  edit: () => boolean;
  peek: () => boolean;
  selectRow: () => boolean;
  fillDown: () => boolean;
  clear: () => boolean;
  openRecord: () => boolean;
  escape: () => boolean;
  selectAll: () => void;
  /** ⇧F10: the column menu of the active column. */
  columnMenu: () => boolean;
};

export const gridScope = defineScope<GridCommandContext>({
  id: 'views.grid',
  kind: 'grid',
  title: () => t('views.grid.scope'),
});

export type ListCommandContext = { focusSearch: () => boolean };

/**
 * The list screen around every layout. A list-level scope (like the record page's), not a route
 * scope: its server undo then never stacks with an editor's or a record's own ⌘Z (plan §4.2 rule 4),
 * and the focused grid, sorted before it, still falls through to it for ⌘F and ⌘Z.
 */
export const listScope = defineScope<ListCommandContext>({
  id: 'views.list',
  kind: 'list',
  title: () => t('views.grid.listScope'),
});

const nav = () => t('views.grid.group.navigate');
const sel = () => t('views.grid.group.select');
const edit = () => t('views.grid.group.edit');

type MoveDef = [id: string, title: () => string, key: string, move: Move, extend: boolean, hidden?: boolean];

const MOVES: MoveDef[] = [
  ['up', () => t('views.grid.cmd.up'), 'arrowup', { kind: 'step', dRow: -1, dCol: 0 }, false],
  ['down', () => t('views.grid.cmd.down'), 'arrowdown', { kind: 'step', dRow: 1, dCol: 0 }, false],
  ['left', () => t('views.grid.cmd.left'), 'arrowleft', { kind: 'step', dRow: 0, dCol: -1 }, false],
  ['right', () => t('views.grid.cmd.right'), 'arrowright', { kind: 'step', dRow: 0, dCol: 1 }, false],
  ['top', () => t('views.grid.cmd.top'), 'mod+arrowup', { kind: 'edge', dRow: -1, dCol: 0 }, false],
  ['bottom', () => t('views.grid.cmd.bottom'), 'mod+arrowdown', { kind: 'edge', dRow: 1, dCol: 0 }, false],
  ['first', () => t('views.grid.cmd.rowStart'), 'mod+arrowleft', { kind: 'edge', dRow: 0, dCol: -1 }, false],
  ['last', () => t('views.grid.cmd.rowEnd'), 'mod+arrowright', { kind: 'edge', dRow: 0, dCol: 1 }, false],
  ['home', () => t('views.grid.cmd.rowStart'), 'home', { kind: 'rowStart' }, false, true],
  ['end', () => t('views.grid.cmd.rowEnd'), 'end', { kind: 'rowEnd' }, false, true],
  ['gridStart', () => t('views.grid.cmd.gridStart'), 'mod+home', { kind: 'first' }, false],
  ['gridEnd', () => t('views.grid.cmd.gridEnd'), 'mod+end', { kind: 'last' }, false],
  ['pageUp', () => t('views.grid.cmd.pageUp'), 'pageup', { kind: 'page', dir: -1 }, false],
  ['pageDown', () => t('views.grid.cmd.pageDown'), 'pagedown', { kind: 'page', dir: 1 }, false],
  ['extendUp', () => t('views.grid.cmd.extend'), 'shift+arrowup', { kind: 'step', dRow: -1, dCol: 0 }, true],
  [
    'extendDown',
    () => t('views.grid.cmd.extend'),
    'shift+arrowdown',
    { kind: 'step', dRow: 1, dCol: 0 },
    true,
    true,
  ],
  [
    'extendLeft',
    () => t('views.grid.cmd.extend'),
    'shift+arrowleft',
    { kind: 'step', dRow: 0, dCol: -1 },
    true,
    true,
  ],
  [
    'extendRight',
    () => t('views.grid.cmd.extend'),
    'shift+arrowright',
    { kind: 'step', dRow: 0, dCol: 1 },
    true,
    true,
  ],
  [
    'extendTop',
    () => t('views.grid.cmd.extendEdge'),
    'mod+shift+arrowup',
    { kind: 'edge', dRow: -1, dCol: 0 },
    true,
  ],
  [
    'extendBottom',
    () => t('views.grid.cmd.extendEdge'),
    'mod+shift+arrowdown',
    { kind: 'edge', dRow: 1, dCol: 0 },
    true,
    true,
  ],
  [
    'extendFirst',
    () => t('views.grid.cmd.extendEdge'),
    'mod+shift+arrowleft',
    { kind: 'edge', dRow: 0, dCol: -1 },
    true,
    true,
  ],
  [
    'extendLast',
    () => t('views.grid.cmd.extendEdge'),
    'mod+shift+arrowright',
    { kind: 'edge', dRow: 0, dCol: 1 },
    true,
    true,
  ],
];

const moveCommands = MOVES.map(([id, title, key, move, extend, hidden]) =>
  defineCommand<GridCommandContext>({
    id: `views.grid.${id}`,
    title,
    shortcut: key,
    scope: gridScope,
    repeat: true,
    group: extend ? sel : nav,
    ...(hidden ? { hidden: true } : {}),
    run: (ctx) => ctx.move(move, extend),
  }),
);

export const gridCommands = [
  ...moveCommands,
  defineCommand<GridCommandContext>({
    id: 'views.grid.tab',
    title: () => t('views.grid.cmd.next'),
    shortcut: 'tab',
    scope: gridScope,
    group: nav,
    hidden: true,
    run: (ctx) => ctx.tab(1),
  }),
  defineCommand<GridCommandContext>({
    id: 'views.grid.shiftTab',
    title: () => t('views.grid.cmd.previous'),
    shortcut: 'shift+tab',
    scope: gridScope,
    group: nav,
    hidden: true,
    run: (ctx) => ctx.tab(-1),
  }),
  defineCommand<GridCommandContext>({
    id: 'views.grid.edit',
    title: () => t('views.grid.cmd.edit'),
    icon: 'pencil',
    shortcut: ['enter', 'f2'],
    scope: gridScope,
    group: edit,
    run: (ctx) => ctx.edit(),
  }),
  defineCommand<GridCommandContext>({
    id: 'views.grid.peek',
    title: () => t('views.grid.cmd.peek'),
    icon: 'eye',
    shortcut: 'space',
    scope: gridScope,
    group: nav,
    run: (ctx) => ctx.peek(),
  }),
  defineCommand<GridCommandContext>({
    id: 'views.grid.selectRow',
    title: () => t('views.grid.cmd.selectRow'),
    shortcut: 'shift+space',
    scope: gridScope,
    group: sel,
    run: (ctx) => ctx.selectRow(),
  }),
  defineCommand<GridCommandContext>({
    id: 'views.grid.fillDown',
    title: () => t('views.grid.cmd.fillDown'),
    icon: 'copy',
    shortcut: 'mod+d',
    scope: gridScope,
    group: edit,
    run: (ctx) => ctx.fillDown(),
  }),
  defineCommand<GridCommandContext>({
    id: 'views.grid.clear',
    title: () => t('views.grid.cmd.clear'),
    icon: 'trash',
    shortcut: ['delete', 'backspace'],
    scope: gridScope,
    group: edit,
    run: (ctx) => ctx.clear(),
  }),
  defineCommand<GridCommandContext>({
    id: 'views.grid.open',
    title: () => t('views.grid.cmd.open'),
    icon: 'external',
    shortcut: 'mod+enter',
    scope: gridScope,
    group: nav,
    run: (ctx) => ctx.openRecord(),
  }),
  defineCommand<GridCommandContext>({
    id: 'views.grid.selectAll',
    title: () => t('views.grid.cmd.selectAll'),
    shortcut: 'mod+a',
    scope: gridScope,
    group: sel,
    run: (ctx) => ctx.selectAll(),
  }),
  defineCommand<GridCommandContext>({
    id: 'views.grid.columnMenu',
    title: () => t('views.grid.cmd.columnMenu'),
    shortcut: 'shift+f10',
    scope: gridScope,
    group: nav,
    run: (ctx) => ctx.columnMenu(),
  }),
  defineCommand<GridCommandContext>({
    id: 'views.grid.escape',
    title: () => t('views.grid.cmd.escape'),
    shortcut: 'escape',
    scope: gridScope,
    group: sel,
    hidden: true,
    run: (ctx) => ctx.escape(),
  }),
];

export const listCommands = [
  defineCommand<ListCommandContext>({
    id: 'views.list.search',
    title: () => t('views.grid.cmd.search'),
    icon: 'search',
    shortcut: 'mod+f',
    scope: listScope,
    run: (ctx) => ctx.focusSearch(),
  }),
  ...serverUndoCommands(listScope),
];

export default defineAppCommands({
  id: 'views.grid',
  scopes: [listScope, gridScope],
  commands: [...listCommands, ...gridCommands],
});
